import {
  calculateServiceBillingLine,
  calculateServiceBillingTotals,
  validateServiceBillingProposal,
  type MarkServiceBillingProposalInvoicedInput,
  type ReturnServiceBillingProposalInput,
  type SaveServiceBillingProposalInput,
  type ServiceBillingGateway,
  type ServiceBillingLine,
  type ServiceBillingProposal,
  type ServiceBillingProposalStatus,
} from './index';

function clone<T>(value: T): T { return structuredClone(value); }

export class DemoServiceBillingGateway implements ServiceBillingGateway {
  private proposals: ServiceBillingProposal[] = [];

  async listProposals(tenantId: string, status: ServiceBillingProposalStatus | 'all' = 'all') {
    return clone(this.proposals.filter((proposal) => proposal.tenantId === tenantId && (status === 'all' || proposal.status === status)));
  }

  async saveAndSubmit(input: SaveServiceBillingProposalInput): Promise<ServiceBillingProposal> {
    validateServiceBillingProposal(input);
    const existing = this.proposals.find((proposal) => proposal.tenantId === input.tenantId && proposal.serviceJobId === input.serviceJobId && proposal.status !== 'returned');
    if (existing) return clone(existing);
    const previousVersions = this.proposals.filter((proposal) => proposal.tenantId === input.tenantId && proposal.serviceJobId === input.serviceJobId);
    const lines: ServiceBillingLine[] = input.lines.map((line) => ({
      ...line,
      id: crypto.randomUUID(),
      netAmount: calculateServiceBillingLine(line),
    }));
    const totals = calculateServiceBillingTotals(lines);
    const proposal: ServiceBillingProposal = {
      id: crypto.randomUUID(), tenantId: input.tenantId, serviceJobId: input.serviceJobId,
      serviceNumber: input.serviceNumber, orderId: input.orderId, orderNumber: input.orderNumber,
      customerId: input.customerId ?? null, customerNumber: input.customerNumber ?? '', customerName: input.customerName,
      invoiceAddress: input.invoiceAddress, machineLabels: [...input.machineLabels], serviceDate: input.serviceDate,
      technicianNames: [...input.technicianNames], workPerformed: input.workPerformed, findings: input.findings?.trim() ?? '',
      paperPrintDocumentId: input.paperPrintDocumentId ?? null, customerSignatureOnPaper: input.customerSignatureOnPaper,
      lines, ...totals, status: 'ready', version: previousVersions.length + 1,
      preparedBy: input.preparedBy, preparedAt: new Date().toISOString(), returnedReason: '', returnedAt: null, invoicedNumber: '',
    };
    this.proposals.unshift(proposal);
    return clone(proposal);
  }

  async returnToService(input: ReturnServiceBillingProposalInput): Promise<ServiceBillingProposal> {
    if (!input.reason.trim()) throw new Error('Bitte einen Rückgabegrund angeben.');
    const index = this.proposals.findIndex((proposal) => proposal.id === input.proposalId && proposal.tenantId === input.tenantId);
    if (index < 0) throw new Error('Rechnungsvorschlag wurde nicht gefunden.');
    const current = this.proposals[index];
    if (current.status !== 'ready') throw new Error('Nur bereitgestellte Vorschläge können zurückgegeben werden.');
    const updated = { ...current, status: 'returned' as const, returnedReason: input.reason.trim(), returnedAt: new Date().toISOString() };
    this.proposals[index] = updated;
    return clone(updated);
  }

  async markInvoiced(input: MarkServiceBillingProposalInvoicedInput): Promise<ServiceBillingProposal> {
    if (!input.invoiceNumber.trim()) throw new Error('Rechnungsnummer fehlt.');
    const index = this.proposals.findIndex((proposal) => proposal.id === input.proposalId && proposal.tenantId === input.tenantId);
    if (index < 0) throw new Error('Rechnungsvorschlag wurde nicht gefunden.');
    const current = this.proposals[index];
    if (current.status !== 'ready') throw new Error('Nur bereitgestellte Vorschläge können fakturiert werden.');
    const updated = { ...current, status: 'invoiced' as const, invoicedNumber: input.invoiceNumber.trim() };
    this.proposals[index] = updated;
    return clone(updated);
  }
}

export function createDemoServiceBillingGateway(): ServiceBillingGateway {
  return new DemoServiceBillingGateway();
}
